import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';

function formatLoadError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const parts = d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x)));
    return parts.join('; ') || 'Failed to load changelog';
  }
  return err.message || 'Failed to load changelog';
}

const Changelog = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data: res } = await axios.get('/settings/changelog');
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(formatLoadError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight={700}>
        Changelog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Release history for Backlot. Entries are parsed from <code>CHANGELOG.md</code> in the running API
        container.
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : null}
      {!loading && data ? (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Running API version
            </Typography>
            <Chip label={data.app_version} size="small" sx={{ mt: 0.5 }} />
          </Paper>
          {data.releases.length === 0 ? (
            <Alert severity="info">No releases found in the changelog file.</Alert>
          ) : (
            data.releases.map((rel, relIdx) => (
              <Accordion
                key={`${rel.version}-${rel.date}`}
                defaultExpanded={relIdx === 0}
                disableGutters
                sx={{ '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography component="span" fontWeight={700}>
                      {rel.version}
                    </Typography>
                    <Chip label={rel.date} size="small" variant="outlined" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {rel.sections.map((sec, secIdx) => (
                    <Box
                      key={`${rel.version}-${sec.title}-${secIdx}`}
                      sx={{ mb: secIdx === rel.sections.length - 1 ? 0 : 2 }}
                    >
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {sec.title}
                      </Typography>
                      {sec.items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          (no bullet items)
                        </Typography>
                      ) : (
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {sec.items.map((item, i) => (
                            <li key={i}>
                              <Typography variant="body2" component="span">
                                {item}
                              </Typography>
                            </li>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </>
      ) : null}
    </Box>
  );
};

export default Changelog;
